import { useDateFormat, useTranslation } from '../i18n';
import type { Article } from '../api/types';
import { coverUrl, pickPublishDate, publicTags } from '../api/articles';

/**
 * Cinematic full-bleed hero for the single-article view. Cover image
 * fills the band, a gradient scrim provides title legibility, and the
 * title sits over the bottom of the cover in the editorial italic
 * display font. Metadata strip (tags + date + reading time) sits below
 * as mono-uppercase chrome - same vocabulary the catalog uses on its
 * Work detail view.
 *
 * <para>Renders without a cover when <c>article.coverMediaId</c> is
 * unset - the gradient scrim covers the void with a tasteful dark band
 * rather than collapsing the layout.</para>
 */
export function ArticleHero({ article }: { article: Article }) {
    const t = useTranslation();
    const fmt = useDateFormat();
    const cover = coverUrl(article, 'article-hero');
    const tags = publicTags(article);
    const publishedAt = pickPublishDate(article);
    const dateLabel = publishedAt ? fmt(publishedAt) : '';

    return (
        <header class="relative overflow-hidden border-b border-rule bg-surface-2">
            {cover && (
                <img
                    src={cover}
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover opacity-65"
                    loading="eager"
                />
            )}
            <div class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/30" />

            <div class="relative mx-auto max-w-[1280px] px-[clamp(20px,3.5vw,56px)] pt-[clamp(60px,9vw,140px)] pb-[clamp(40px,5vw,80px)]">
                {tags[0] && (
                    <div class="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                        <span>{tags[0]}</span>
                    </div>
                )}

                <h1 class="mt-3 max-w-[24ch] font-display text-[clamp(36px,5vw,68px)] italic font-normal leading-[1.05] text-fg-primary drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                    {article.title ?? t('articles.untitled')}
                </h1>

                {article.subtitle && (
                    <p class="mt-4 max-w-[60ch] font-display text-[clamp(17px,1.4vw,22px)] italic font-normal text-fg-primary/85 drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">
                        {article.subtitle}
                    </p>
                )}

                <div class="mt-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                    {dateLabel && <span>{dateLabel}</span>}
                    {dateLabel && <span class="text-fg-faint">·</span>}
                    <span>
                        {t('articles.readingMinutes', { n: article.readingMinutes || 1 })}
                    </span>
                </div>
            </div>
        </header>
    );
}
